import { z } from 'zod';

const MetadataSchema = z.object({
  title: z.string().nullable().optional(),
  description: z.string().nullable().optional(),
  keywords: z.string().nullable().optional(),
  ogTitle: z.string().nullable().optional(),
  ogImage: z.string().nullable().optional(),
});

const ExtractedValueSchema = z.string().nullable().optional();
const ExtractedSchema = z.record(z.string(), ExtractedValueSchema);

const ContextResultSchema = z.object({
  pageType: z.string(),
  confidence: z.number(),
  metadata: MetadataSchema,
  keywords: z.array(z.string()),
  extracted: z.record(z.string(), ExtractedValueSchema).optional(),
  raw: z.record(z.string(), z.unknown()),
  detectedAt: z.string().optional(),
});

export const ExtractorFunctionSchema = z.function({
  input: [z.instanceof(Document)],
  output: z.string(),
});

const PageTypeConfigSchema = z.object({
  name: z.string(),
  urlPatterns: z.array(z.instanceof(RegExp)).optional(),
  domSelectors: z.array(z.string()).optional(),
  extractors: z.record(z.string(), ExtractorFunctionSchema).optional(),
  onMatch: z
    .function({
      input: [ContextResultSchema.optional()],
      output: z.void(),
    })
    .optional(),
});

const ConfigSchema = z.record(z.string(), PageTypeConfigSchema);

export const ContextEngineSchemas = {
  Metadata: MetadataSchema,
  ExtractedValue: ExtractedValueSchema,
  Extracted: ExtractedSchema,
  ContextResult: ContextResultSchema,
  PageTypeConfig: PageTypeConfigSchema,
  Config: ConfigSchema,
  ExtractorFunction: ExtractorFunctionSchema,
};

export namespace ContextEngine {
  export type Metadata = z.infer<typeof MetadataSchema>;
  export type ExtractedValue = z.infer<typeof ExtractedValueSchema>;
  export type Extracted = z.infer<typeof ExtractedSchema>;
  export type ContextResult = z.infer<typeof ContextResultSchema>;
  export type PageTypeConfig = z.infer<typeof PageTypeConfigSchema>;
  export type Config = z.infer<typeof ConfigSchema>;
  export type ExtractDomElementTextFn = z.infer<typeof ExtractorFunctionSchema>;

  export interface ContextDetectorOptions {
    pageTypes?: Config;
    cacheTTL?: number;
    analytics?: { log: (event: string, payload: unknown) => void } | null;
    timeoutMs?: number;
    baseOrigin?: string;
    storage?: Storage | null;
    disableCache?: boolean;
  }
}

export const CustomContextSchema = z.record(z.string(), z.unknown());

export type CustomContext = z.infer<typeof CustomContextSchema>;
